import MarketTypes from 'modules/market/types';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { isString, omit, curry, isEmpty } from 'mojito/utils';

const { HEADER_CONTENT, LAYOUT_TYPE } = MarketTypes;
const { TWO_WAY, THREE_WAY } = LAYOUT_TYPE;
const { SortingHelper } = MojitoPresentation.Utils;
const EventUtils = MojitoServices.SportsContent.Events.utils;
const { sortParticipants } = MojitoPresentation.Components.scoreboardUtils;

export const SELECTION_TYPE_PROP = 'type';
export const SELECTION_NAME_PROP = 'name';
export const SELECTION_POSITION_PROP = 'position';

const HOME_TYPES = ['H', '1', 'P1', '1X'];
const AWAY_TYPES = ['A', '2', 'P2', 'X2'];
const UNKNOWN = 'UNKNOWN';

const selectionMatcher = key => ({
    match: (value, selection) => value === selection[key],
    propName: key,
});
const matchName = selectionMatcher(SELECTION_NAME_PROP);
const matchType = selectionMatcher(SELECTION_TYPE_PROP);
const matchPosition = selectionMatcher(SELECTION_POSITION_PROP);

/**
 * Helper class offers functionality for aggregated market view.
 *
 * @class MarketHelper
 * @memberof Mojito.Modules
 */

/**
 * Resolves headers typically used for selection groups titling within aggregated market.
 *
 * @function resolveSelectionsHeaders
 *
 * @param {object} descriptor - Market layout descriptor.
 * @param {object} event - Event data object.
 * @param {Mojito.Core.Base.StringResolver} stringResolver - Instance of string resolver.
 * @param {boolean} [shouldShowDrawHeader = true] - Flag indicates if the header for draw result should be shown.
 * @param {string} [handicap = ''] - Handicap value.
 *
 * @returns {Array<string>} List of selection headers.
 * @memberof Mojito.Modules.MarketHelper
 */
export const resolveSelectionsHeaders = (
    descriptor,
    event,
    stringResolver,
    shouldShowDrawHeader = true,
    handicap = ''
) => {
    const { selectionHeadersMap = {}, selectionTypes = [] } = descriptor;
    switch (descriptor.selectionsHeaderContent) {
        case HEADER_CONTENT.PARTICIPANT:
            return resolveHeadersForParticipants(
                descriptor,
                event,
                stringResolver,
                shouldShowDrawHeader
            );
        case HEADER_CONTENT.TYPE:
            return selectionTypes.map(type =>
                stringResolver
                    .resolveAndFormatString(selectionHeadersMap[type], handicap)
                    ?.trimEnd()
            );
    }
    return [];
};

/**
 * Resolves selection groups for the market based on the provided <code>groupingMask</code>.
 * Typically used to display selections in multiple columns which reflect betting ways.
 * This function guarantee line integrity with "bogus" selections.
 * Example: If three way market has groupingMask: [1, X, 2] and there are three selections exist on a market then three
 * groups will be generated per each bet way with single selection in each group. If for the same market only two selections exist
 * then missing selection will be replaced with "bogus" selection object to ensure proper three way layout.
 * If provided <code>groupingMask</code> doesn't comply with real selection types existing on selection objects and strictGrouping is enabled then selections will be equally divided by groups using numberOfSelectionGroups param.
 *
 * @function resolveSelectionGroups
 *
 * @param {Mojito.Modules.MarketHelper.SelectionMatcher} matcher - Selection mather instance. Contains predicate function. If returns true then selection matches grouping criteria
 * provided in <code>groupingMask</code> property and will be added to corresponding group.
 * @param {object} market - Market data object. Selections from this object will be grouped. Note: this function does not mutate market object.
 * @param {Array<string>} groupingMask - List of groups identifiers. Will be used as criteria to generate selection group per identifier.
 * Selections will be organized between groups according to this mask. The groups order will reflect the order of corresponding identifiers withing mask list.
 * @param {boolean} [switchHomeAway = false] - True if home and away groups are supposed to switch places.
 * Mainly used in american sports where home/away selections have reverse order within market line.
 * @param {number} [numberOfSelectionGroups = 1] - Number of market groups columns. It will only be used if the groupingMask is empty - otherwise it will be based on the groupingMask.
 * @param {boolean} [strictGrouping = false] - If true then function will validate if <code>groupingMask</code> complies with real selection types existing on selection objects and will apply mask only if the result of such checking is positive.
 *
 * @returns {Array<Array<object>>} List of selection groups. Each selection group is a list of selections.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const resolveSelectionGroups = (
    matcher,
    market,
    groupingMask,
    switchHomeAway = false,
    numberOfSelectionGroups = 1,
    strictGrouping = false
) => {
    const selections = (market && market.selections && market.selections.slice()) || [];
    const selectionsComparator = SortingHelper.composeComparators(
        EventUtils.compareBySortOrder,
        EventUtils.compareByPrice
    );
    selections.sort(selectionsComparator);
    const result = isApplicableMask(groupingMask, selections, matcher, strictGrouping)
        ? generateLine(selections, groupingMask, matcher)
        : splitIntoGroups(selections, numberOfSelectionGroups);

    return switchHomeAway && isHomeAwayLine(getLayoutType(groupingMask), result)
        ? swapHomeAwayGroups(result)
        : result;
};

/**
 * This function is like {@link Mojito.Modules.MarketHelper.resolveSelectionGroups} except that
 * it matches <code>groupingMask</code> values to <code>selection.name</code> property. It allows to group selections by desired names mask.
 *
 * @function resolveSelectionGroupsByName
 *
 * @param {object} market - Market data object. Selections from this object will be grouped.
 * @param {Array<string>} groupingMask - List of selection names. Will be used as criteria to generate selection group per name.
 * @param {boolean} switchHomeAway - True if home and away groups are suppose to switch places.
 * @param {number} [numberOfSelectionGroups = 1] - Number of market groups columns. It will only be used if the groupingMask is empty - otherwise it will be based on the groupingMask.
 * @param {boolean} [strictGrouping = false] - If true then function will validate if <code>groupingMask</code> complies with real selection types existing on selection objects and will apply mask only if the result of such checking is positive.
 *
 * @returns {Array<Array<object>>} List of selection groups. Each selections group is a list of selections.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const resolveSelectionGroupsByName = curry(resolveSelectionGroups)(matchName);

/**
 * This function is like {@link Mojito.Modules.MarketHelper.resolveSelectionGroups} except that
 * it matches <code>groupingMask</code> values to <code>selection.type</code> property. It allows to group selections by desired types mask.
 *
 * @function resolveSelectionGroupsByType
 *
 * @param {object} market - Market data object. Selections from this object will be grouped.
 * @param {Array<string>} groupingMask - List of selection types. Will be used as criteria to generate selection group per type.
 * @param {boolean} switchHomeAway - True if home and away groups are suppose to switch places.
 * @param {number} [numberOfSelectionGroups = 1] - Number of market groups columns. It will only be used if the groupingMask is empty - otherwise it will be based on the groupingMask.
 * @param {boolean} [strictGrouping = false] - If true then function will validate if <code>groupingMask</code> complies with real selection types existing on selection objects and will apply mask only if the result of such checking is positive.
 *
 * @returns {Array<Array<object>>} List of selection groups. Each selections group is a list of selections.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const resolveSelectionGroupsByType = curry(resolveSelectionGroups)(matchType);

/**
 * This function is like {@link Mojito.Modules.MarketHelper.resolveSelectionGroups} except that
 * it matches <code>groupingMask</code> values to <code>selection.position</code> property. It allows to group selections by desired position mask.
 *
 * @function resolveSelectionGroupsByPosition
 *
 * @param {object} market - Market data object. Selections from this object will be grouped.
 * @param {Array<string>} groupingMask - List of selection types. Will be used as criteria to generate selection group per type.
 * @param {boolean} switchHomeAway - True if home and away groups are suppose to switch places.
 * @param {number} [numberOfSelectionGroups = 1] - Number of market groups columns. It will only be used if the groupingMask is empty - otherwise it will be based on the groupingMask.
 * @param {boolean} [strictGrouping = false] - If true then function will validate if <code>groupingMask</code> complies with real selection types existing on selection objects and will apply mask only if the result of such checking is positive.
 *
 * @returns {Array<Array<object>>} List of selection groups. Each selections group is a list of selections.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const resolveSelectionGroupsByPosition = curry(resolveSelectionGroups)(matchPosition);

/**
 * Splits selections into groups.
 *
 * @function splitIntoGroups
 *
 * @param {Array<object>} selections - Selections list.
 * @param {number} groupsCount - Number of desired groups.
 *
 * @returns {Array<Array<object>>} List of selection groups. Each selections group is a list of selections.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const splitIntoGroups = (selections, groupsCount) => {
    // Free form markets always present selections in predefined number of columns based on the amount of groups.
    // This property is configurable per market type, market category and has default value.
    const result = new Array(groupsCount).fill().map(() => []);
    selections.forEach((selection, index) => {
        const groupIndex = index % groupsCount;
        result[groupIndex].push(selection);
    });
    return selections.length ? result : [];
};

/**
 * Resolves market layout descriptor. Helpful for rendering market selections and headers.
 *
 * @function resolveMarketLayout
 *
 * @param {object} market - Event object.
 * @param {object} layoutDescriptors - Participant index in participants list.
 * @param {string} [sportId] - Sport id. If sport specific market type category
 * is present in layoutDescriptors it will have highest precedence when resolving
 * the market layout.
 *
 * @returns {object} Market layout descriptor.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const resolveMarketLayout = (market, layoutDescriptors, sportId) => {
    let marketLayout = {};
    if (market) {
        const sportSpecificCategories = layoutDescriptors.sportSpecificCategories[sportId] || {};
        const sportSpecificCategoryLayout = sportSpecificCategories[market.typeCategory] || {};
        marketLayout = {
            ...layoutDescriptors.default,
            ...layoutDescriptors.categories[market.typeCategory],
            ...layoutDescriptors.types[market.type],
            ...sportSpecificCategoryLayout,
        };
    }
    return {
        ...marketLayout,
        ...omit(
            layoutDescriptors,
            'categories',
            'types',
            'selectionHeaders',
            'sportSpecificCategories'
        ),
        selectionHeadersMap: layoutDescriptors.selectionHeaders,
    };
};

/**
 * Gets participant from event.
 *
 * @function getParticipant
 *
 * @param {object} event - Event object.
 * @param {number} index - Participant index in participants list.
 *
 * @returns {object} Participant.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const getParticipant = (event, index) => {
    const participants = sortParticipants(event?.participants, event?.homeAway);
    return participants?.[index];
};

/**
 * Gets participant name from event.
 *
 * @function getParticipantName
 *
 * @param {object} event - Event object.
 * @param {number} index - Participant index in participants list.
 *
 * @returns {string} Participant name.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const getParticipantName = (event, index) => {
    return getParticipant(event, index)?.name ?? '';
};

/**
 * Checks if provided groupingMask can be used to split selections into a groups by selection.type.
 * The function will return true if every selection in a market has type which matches to at least one selection type in a groupingMask list.
 *
 * @function isApplicableMaskByType
 *
 * @param {Array<string>} groupingMask - List of selection types.
 * @param {object} market - Market object.
 *
 * @returns {boolean} Flag indicating if groupingMask is applicable to a market.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const isApplicableMaskByType = (groupingMask, market) => {
    const selections = market?.selections?.slice() || [];
    return isApplicableMask(groupingMask, selections, matchType, true);
};

/**
 * Resolves the share of bets placed on each selection of a market, based on the `betCount` of every selection.
 * A selection without `betCount` is counted as having no bets.
 * Shares are whole percentages which always sum up to 100.
 *
 * @function getBetDistribution
 *
 * @param {object} market - Market object.
 * @param {number} minimumBetCount - Minimum number of bets across the market selections required to resolve the distribution. If -1 then there is no minimum.
 *
 * @returns {Object<string, Mojito.Modules.SelectionButton.types.BetDistribution>|undefined} Bet distribution keyed by selection id, or undefined if there are no bets or the total number of bets is below <code>minimumBetCount</code>.
 *
 * @memberof Mojito.Modules.MarketHelper
 */
export const getBetDistribution = (market, minimumBetCount) => {
    const counts = (market?.selections || []).map(({ id, betCount }) => ({
        id,
        count: Number.isFinite(betCount) ? betCount : 0,
    }));
    const total = counts.reduce((sum, { count }) => sum + count, 0);
    const isLimited = minimumBetCount !== -1;
    if (total > 0 && (!isLimited || total >= minimumBetCount)) {
        // Largest remainder rounding keeps the whole percentages summing up to exactly 100.
        const shares = counts.map(({ id, count }) => {
            const exact = (count * 100) / total;
            return { id, percent: Math.floor(exact), remainder: exact - Math.floor(exact) };
        });
        const missing = 100 - shares.reduce((sum, { percent }) => sum + percent, 0);
        [...shares]
            .sort((first, second) => second.remainder - first.remainder)
            .slice(0, missing)
            .forEach(share => share.percent++);
        return Object.fromEntries(shares.map(({ id, percent }) => [id, { percent }]));
    }
};

/**
 * Checks if selection is linked to a home team.
 *
 * @function isHomeTeam
 *
 * @param {object} selection - Selection object.
 *
 * @returns {boolean} Flag indicating if selection is a home team.
 */
export const isHomeTeam = selection => {
    return HOME_TYPES.includes(selection.type);
};

/**
 * Checks if selection is linked to a away team.
 *
 * @function isAwayTeam
 *
 * @param {object} selection - Selection object.
 *
 * @returns {boolean} Flag indicating if selection is a away team.
 */
export const isAwayTeam = selection => {
    return AWAY_TYPES.includes(selection.type);
};

const resolveHeadersForParticipants = (descriptor, event, stringResolver, shouldShowDrawHeader) => {
    if (!event) {
        return [];
    }

    const { selectionHeadersMap = {}, selectionTypes = [], showDrawHeader = false } = descriptor;
    const isThreeWay = getLayoutType(selectionTypes) === THREE_WAY;
    const drawl10n = showDrawHeader
        ? '$GENERIC_MARKET_CONTENT.SELECTION_HEADER.DRAW'
        : selectionHeadersMap[selectionTypes[1]];
    // Note: US sports participants are switched in events slice once received from content so participant[0] will in that case
    // be the away team. The result is that the code below will map '0' to the away team for US Sports.
    // This is the intended behavior.
    const firstParticipant = getParticipantName(event, 0);
    const draw =
        ((isThreeWay && shouldShowDrawHeader) || showDrawHeader) &&
        stringResolver.resolveString(drawl10n);
    const secondParticipant = getParticipantName(event, 1);
    return [firstParticipant, draw, secondParticipant].filter(isString);
};

const generateLine = (selections, groupingMask, matcher) => {
    const bogusGenerator = bogusSelectionGenerator();
    const { match, propName } = matcher;
    return groupingMask.map(criterion => {
        const bogusProto = { [propName]: criterion };
        return resolveSelections(selections, bogusGenerator(bogusProto), sel =>
            match(criterion, sel)
        );
    });
};

const resolveSelections = (selections, generateBogus, predicate) => {
    const foundSelections = selections.filter(predicate);
    foundSelections.sort(EventUtils.compareBySortOrder);
    return foundSelections.length ? foundSelections : [generateBogus()];
};

const isHomeAwayLine = (layoutType, groupsLine) => {
    const isTwoWayOrThreeWay = layoutType === TWO_WAY || layoutType === THREE_WAY;
    if (isTwoWayOrThreeWay) {
        const isHomeType = groupHasType(groupsLine, 0, HOME_TYPES);
        const isAwayType = groupHasType(groupsLine, groupsLine.length - 1, AWAY_TYPES);
        return isHomeType || isAwayType;
    }
    return false;
};

const isApplicableMask = (groupingMask, selections, matcher, strictGrouping) => {
    if (isEmpty(groupingMask)) {
        return false;
    }
    const { match } = matcher;
    return strictGrouping
        ? selections.every(sel => groupingMask.find(criterion => match(criterion, sel)))
        : true;
};

/**
 * This method swaps places of home/away selection groups.
 * This is typically needed for american sports where away participant is shown as first one and home participant as last one.
 * To make coupons look consistent the selections are also swapped within markets respectively.
 * E.g., If market has selections with types [H, A], this method will swap them to be [A, H].
 * For three way markets: [H, A, X] -> [A, H, X]. The draw selection place always stays untouched.
 * Note: this method mutates groupsLine parameter.
 *
 * @function swapHomeAwayGroups
 * @param {Array<Array<object>>} groupsLine - Groups line. List of selection groups. Each selections group is a list of selections.
 *
 * @returns {Array<Array<object>>} List of selection groups. Each selections group is a list of selections.
 * @private
 *
 * @memberof Mojito.Modules.MarketHelper
 */
const swapHomeAwayGroups = groupsLine => {
    const homeGroup = findGroupInTypes(HOME_TYPES, groupsLine);
    const awayGroup = findGroupInTypes(AWAY_TYPES, groupsLine);
    const homeIndex = groupsLine.indexOf(homeGroup);
    const awayIndex = groupsLine.indexOf(awayGroup);
    groupsLine[awayIndex] = homeGroup;
    groupsLine[homeIndex] = awayGroup;
    return groupsLine;
};

const findGroupInTypes = (types, groupsLine) => {
    let result;
    types.some(type => {
        result = findGroupByType(groupsLine, type);
        return !!result;
    });
    return result;
};

/**
 * Generator that is used to create bogus selection objects.
 * This kind of objects will be created in place of missing selections in order to keep
 * integrity in selections line. E.g., Match betting market has selections mask: [1, X, 2].
 * If 'X' selection is missing from content it will be replaced with BOGUS selection with type: 'X'.
 *
 * @function bogusSelectionGenerator
 *
 * @returns {Function} Generator function.
 * @private
 *
 * @memberof Mojito.Modules.MarketHelper
 */
const bogusSelectionGenerator = () => {
    let index = 0;
    return (bogusProto = {}) => {
        return () => {
            index++;
            return { id: `BOGUS-${index}`, type: UNKNOWN, ...bogusProto };
        };
    };
};

const groupHasType = (groups, index, types) => {
    const group = groups[index] || [];
    return group.some(selection => types.includes(selection.type));
};

const findGroupByType = (groups, type) =>
    groups.find(selectionsGroup => selectionsGroup.some(selection => selection.type === type));

const getLayoutType = selectionTypes => (isEmpty(selectionTypes) ? 0 : selectionTypes.length);

/**
 * Matcher object that contains match function and selection property name
 * which value will be used during comparison.
 *
 * @typedef SelectionMatcher
 *
 * @property {Mojito.Modules.MarketHelper.selectionMatches} match - Selection match function.
 * @property {string} propName - Name of the selection property, e.g., `type`, `name` etc.
 *
 * @memberof Mojito.Modules.MarketHelper
 */

/**
 * Function that checks if selection matches criteria.
 *
 * @callback selectionMatches
 *
 * @property {*} groupingCriterion - Value that defines criteria which needs to be fulfilled by selection to be added to the group.
 * @property {object} selection - Selection data object.
 * @returns {boolean} True if selection matches criteria, false otherwise.
 *
 * @memberof Mojito.Modules.MarketHelper
 */

export default {
    resolveSelectionsHeaders,
    resolveSelectionGroupsByName,
    resolveSelectionGroupsByType,
    resolveSelectionGroups,
    splitIntoGroups,
    getParticipantName,
    getParticipant,
    isApplicableMaskByType,
    resolveMarketLayout,
    getBetDistribution,
    isHomeTeam,
    isAwayTeam,
};
